import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { MainLayout } from './components/shared/MainLayout'
import { useAuthStore } from './stores/authStore'

// Screens
import { LoginScreen } from './screens/LoginScreen'
import { HomeScreen } from './screens/HomeScreen'
import { RoomsScreen } from './screens/RoomsScreen'
import { WalletScreen } from './screens/WalletScreen'
import { ProfileScreen } from './screens/ProfileScreen'
import { GameTableScreen } from './screens/GameTableScreen'
import { CreateRoomScreen } from './screens/CreateRoomScreen'
import { StatisticsScreen } from './screens/StatisticsScreen'
import { HandHistoryScreen } from './screens/HandHistoryScreen'
import { AgentDashboardScreen } from './screens/AgentDashboardScreen'
import { DisputesScreen } from './screens/DisputesScreen'
import { NotificationHistoryScreen } from './screens/NotificationHistoryScreen'

// Admin screens
import { AdminLayout } from './screens/admin/AdminLayout'
import { AdminDashboardScreen } from './screens/admin/AdminDashboardScreen'
import { AdminUsersScreen } from './screens/admin/AdminUsersScreen'
import { AdminWithdrawalsScreen } from './screens/admin/AdminWithdrawalsScreen'
import { AdminRoomsScreen } from './screens/admin/AdminRoomsScreen'
import { AdminAgentsScreen } from './screens/admin/AdminAgentsScreen'
import { AdminFraudScreen } from './screens/admin/AdminFraudScreen'
import { AdminHandsScreen } from './screens/admin/AdminHandsScreen'
import { AdminFrozenPotsScreen } from './screens/admin/AdminFrozenPotsScreen'
import { AdminHealthScreen } from './screens/admin/AdminHealthScreen'
import { AdminReportsScreen } from './screens/admin/AdminReportsScreen'
import { AdminAuditScreen } from './screens/admin/AdminAuditScreen'
import { AdminMaintenanceScreen } from './screens/admin/AdminMaintenanceScreen'
import { AdminConfigScreen } from './screens/admin/AdminConfigScreen'
import { AdminTransactionsScreen } from './screens/admin/AdminTransactionsScreen'
import { AdminDisputesScreen } from './screens/admin/AdminDisputesScreen'

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const location = useLocation()
  if (!isAuthenticated) return <Navigate to="/login" state={{ from: location.pathname }} replace />
  return <>{children}</>
}

function LoginGuard() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const location = useLocation()
  const from = (location.state as { from?: string })?.from || '/'
  if (isAuthenticated) return <Navigate to={from} replace />
  return <LoginScreen />
}

function App() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

  return (
    <Routes>
      {/* Login */}
      <Route path="/login" element={<LoginGuard />} />

      {/* Main tabbed screens */}
      <Route element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
        <Route path="/" element={<HomeScreen />} />
        <Route path="/rooms" element={<RoomsScreen />} />
        <Route path="/wallet" element={<WalletScreen />} />
        <Route path="/profile" element={<ProfileScreen />} />
        <Route path="/create-room" element={<CreateRoomScreen />} />
      </Route>

      {/* Sub-screens (Full screen) */}
      <Route path="/stats" element={<ProtectedRoute><StatisticsScreen /></ProtectedRoute>} />
      <Route path="/history" element={<ProtectedRoute><HandHistoryScreen /></ProtectedRoute>} />
      <Route path="/agent" element={<ProtectedRoute><AgentDashboardScreen /></ProtectedRoute>} />
      <Route path="/disputes" element={<ProtectedRoute><DisputesScreen /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><NotificationHistoryScreen /></ProtectedRoute>} />

      {/* Full-screen game table - accessible without auth for spectator-first onboarding */}
      <Route path="/room/:roomId" element={<GameTableScreen />} />

      {/* Admin panel */}
      <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
        <Route index element={<AdminDashboardScreen />} />
        <Route path="users" element={<AdminUsersScreen />} />
        <Route path="withdrawals" element={<AdminWithdrawalsScreen />} />
        <Route path="rooms" element={<AdminRoomsScreen />} />
        <Route path="agents" element={<AdminAgentsScreen />} />
        <Route path="fraud" element={<AdminFraudScreen />} />
        <Route path="hands" element={<AdminHandsScreen />} />
        <Route path="frozen-pots" element={<AdminFrozenPotsScreen />} />
        <Route path="health" element={<AdminHealthScreen />} />
        <Route path="reports" element={<AdminReportsScreen />} />
        <Route path="audit" element={<AdminAuditScreen />} />
        <Route path="maintenance" element={<AdminMaintenanceScreen />} />
        <Route path="config" element={<AdminConfigScreen />} />
        <Route path="transactions" element={<AdminTransactionsScreen />} />
        <Route path="disputes" element={<AdminDisputesScreen />} />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to={isAuthenticated ? "/" : "/login"} replace />} />
    </Routes>
  )
}

export default App
